import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';

export interface AssignmentStateModel {
  isLoaded: boolean;
  loadedAssignmentId: number;
  url: string | any;
  refresh: boolean;
  isAssignmentInPendingState: boolean;
  isFrameLoaded: boolean;
  allowedSwitchingUsers: Set<number>; /* empty set means shared with all. null means not shared */
}

export namespace AssignmentAction {
  export class LoadAssignment {
    static readonly type = '[Assignment] load assignment';

    constructor(public url: string, public assignmentId: number) {
    }
  }

  export class UnloadAssignment {
    static readonly type = '[Assignment] unload assignment';

    constructor() {
    }
  }

  export class TogglePendingState {
    static readonly type = '[Assignment] toggle pending state';

    constructor(public isPending: boolean) {
    }
  }

  export class AllowSwitchAccessFor {
    static readonly type = '[Assignment] Allow switch access for user';

    constructor(public userId: number) {
    }
  }

  export class DisallowSwitchAccessFor {
    static readonly type = '[Assignment] Disallow switch access for user';

    constructor(public userId: number) {
    }
  }

  export class AllowSwitchAccessForAll {
    static readonly type = '[Assignment] Allow switch access for all';

    constructor() {
    }
  }

  export class DisallowSwitchAccessForAll {
    static readonly type = '[Assignment] Disallow switch access for all';

    constructor() {
    }
  }

  export class RefreshAssignment {
    static readonly type = '[Assignment] refresh';

    constructor() {
    }
  }

  export class SetFrameIsLoaded {
    static readonly type = '[Assignment] set assignment iframe is loaded';

    constructor() {
    }
  }
}


@State<AssignmentStateModel>({
  name: AssignmentState.storeName,
  defaults: AssignmentState.defaults
})
@Injectable()
export class AssignmentState {
  static readonly storeName = 'assignment';
  static readonly defaults: AssignmentStateModel = {
    isLoaded: false,
    loadedAssignmentId: -1,
    url: null,
    refresh: false,
    isAssignmentInPendingState: false,
    isFrameLoaded: false,
    allowedSwitchingUsers: null,
  };

  @Selector()
  static getAssignmentUrl(state: AssignmentStateModel) {
    return state.url;
  }

  @Action(AssignmentAction.LoadAssignment)
  loadAssignment(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.LoadAssignment) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      url: info.url,
      isLoaded: true,
      loadedAssignmentId: info.assignmentId,
      isFrameLoaded: false,
    });
  }

  @Action(AssignmentAction.TogglePendingState)
  togglePendingState(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.TogglePendingState) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      isAssignmentInPendingState: info.isPending
    });
  }

  @Action(AssignmentAction.AllowSwitchAccessFor)
  allowSwitchAccessFor(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.AllowSwitchAccessFor) {
    const state = ctx.getState();

    if (!state.allowedSwitchingUsers) {
      state.allowedSwitchingUsers = new Set<number>();
    }

    state.allowedSwitchingUsers.add(info.userId);

    ctx.patchState({
      ...state,
      allowedSwitchingUsers: state.allowedSwitchingUsers
    });
  }

  @Action(AssignmentAction.DisallowSwitchAccessFor)
  disallowSwitchAccessFor(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.DisallowSwitchAccessFor) {
    const state = ctx.getState();
    state.allowedSwitchingUsers?.delete(info.userId);

    ctx.patchState({
      ...state,
      allowedSwitchingUsers: state.allowedSwitchingUsers
    });
  }

  @Action(AssignmentAction.AllowSwitchAccessForAll)
  allowSwitchAccessForAll(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.AllowSwitchAccessForAll) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      allowedSwitchingUsers: new Set<number>()
    });
  }

  @Action(AssignmentAction.DisallowSwitchAccessForAll)
  disallowSwitchAccessForAll(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.DisallowSwitchAccessForAll) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      allowedSwitchingUsers: null
    });
  }

  @Action(AssignmentAction.RefreshAssignment)
  refreshAssignment(ctx: StateContext<AssignmentStateModel>) {
    const state = ctx.getState();
    const url = ctx.getState().url;
    ctx.patchState({
      ...state, url: ''
    });

    setTimeout(() => {
      ctx.patchState({
        ...state, url
      });
    }, 1000);
  }

  @Action(AssignmentAction.SetFrameIsLoaded)
  setFrameIsLoaded(ctx: StateContext<AssignmentStateModel>) {
    const state = ctx.getState();
      ctx.patchState({
        ...state,
        isFrameLoaded: true
      });
  }


  @Action(AssignmentAction.UnloadAssignment)
  unloadAssignment(ctx: StateContext<AssignmentStateModel>, info: AssignmentAction.UnloadAssignment) {
    const allowedSwitchingUsers: Set<number> = ctx.getState().allowedSwitchingUsers;

    ctx.patchState(AssignmentState.defaults);

    ctx.patchState({
      allowedSwitchingUsers
    });
  }
}
